import { api } from "@/lib/api";
import {
  ApiResponse,
  ApiResponseWithItems,
  MaintenanceExecutionEntry,
  MaintenanceTask,
} from "@/lib/types";
import { QueryOptions, useQuery } from "@tanstack/react-query";

export interface TaskSearchParams {
  vesselId?: string;
  parentId?: string;
  ancestorId?: string;
  name?: string;
  pageNumber?: number;
  pageSize?: number;
}

export const useGetTasks = (
  params: TaskSearchParams = {},
  options?: QueryOptions<ApiResponseWithItems<MaintenanceTask>["value"]>
) => {
  return useQuery<ApiResponseWithItems<MaintenanceTask>["value"]>({
    queryKey: ["tasks", "tasks-search", params],
    queryFn: async () => {
      const response = await api.get<ApiResponseWithItems<MaintenanceTask>>(
        `/vessels/${params?.vesselId}/maintenance/tasks/search`,
        { params }
      );

      return (
        response.data.value ?? {
          items: [],
          totalCount: 0,
          totalPages: 0,
          pageNumber: 1,
          pageSize: 10,
        }
      );
    },
    enabled: !!params?.vesselId,
    ...options,
  });
};

export const useGetTaskById = (
  params: { vesselId: string; id: string },
  options?: QueryOptions<MaintenanceTask>
) => {
  return useQuery<MaintenanceTask>({
    queryKey: ["task", params?.vesselId, params?.id],
    queryFn: async () => {
      const response = await api.get<ApiResponse<MaintenanceTask>>(
        `/vessels/${params?.vesselId}/maintenance/tasks/${params?.id}`
      );
      return response.data.value;
    },
    enabled: !!params?.vesselId && !!params?.id,
    ...options,
  });
};

export interface MaintenanceExecutionEntrySearchParams {
  taskId?: string;
  vesselId?: string;
  taskNodeId?: string;
  pageNumber?: number;
  pageSize?: number;
  sortBy?: string;
  sortDirection?: string;
}

export const useGetMaintenanceExecutionEntries = (
  params: MaintenanceExecutionEntrySearchParams = {},
  options?: QueryOptions<
    ApiResponseWithItems<MaintenanceExecutionEntry>["value"]
  >
) => {
  return useQuery<ApiResponseWithItems<MaintenanceExecutionEntry>["value"]>({
    queryKey: [
      "maintenance-execution-entries",
      "maintenance-execution-entries-search",
      params,
    ],
    queryFn: async () => {
      const response = await api.get<
        ApiResponseWithItems<MaintenanceExecutionEntry>
      >(
        `/vessels/${params?.vesselId}/maintenance/tasks/${params?.taskId}/execution-entries`,
        { params }
      );
      return (
        response.data.value ?? {
          items: [],
          totalCount: 0,
          totalPages: 0,
          pageNumber: 1,
          pageSize: 10,
        }
      );
    },
    enabled: !!params?.vesselId && !!params?.taskId,
    ...options,
  });
};
